import React, { useEffect, useMemo, useRef, useState } from "react";

const imageResolutionCache = new Map();
const urlProbeCache = new Map();

function buildImageCandidates(legoId) {
  const id = String(legoId || "").trim();
  if (!id) return [];

  const numericOnly = /^\d+$/.test(id);
  const variantId = numericOnly ? `${id}-1` : id;

  const candidates = [
    `https://images.brickset.com/sets/images/${variantId}.jpg`,
    `https://images.brickset.com/sets/images/${id}.jpg`,
    `https://img.bricklink.com/ItemImage/SN/0/${variantId}.png`,
    `https://img.bricklink.com/ItemImage/SN/0/${id}.png`,
    `https://img.bricklink.com/ItemImage/PN/86/${id}.png`,
    `https://img.bricklink.com/ItemImage/PN/11/${id}.png`,
    `https://img.bricklink.com/ItemImage/PN/0/${id}.png`,
  ];

  return Array.from(new Set(candidates));
}

function probeImageUrl(url) {
  if (urlProbeCache.has(url)) {
    return urlProbeCache.get(url);
  }

  const probePromise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

  urlProbeCache.set(url, probePromise);
  return probePromise;
}

function LegoImage({ legoId }) {
  const candidates = useMemo(() => buildImageCandidates(legoId), [legoId]);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState("");
  const [resolutionState, setResolutionState] = useState("idle");
  const cacheKey = String(legoId || "").trim();

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView) return undefined;

    let cancelled = false;

    async function resolveImage() {
      if (!cacheKey || !candidates.length) {
        setResolutionState("missing");
        setResolvedSrc("");
        return;
      }

      const cached = imageResolutionCache.get(cacheKey);
      if (cached) {
        setResolutionState(cached.status === "resolved" ? "resolved" : "missing");
        setResolvedSrc(cached.status === "resolved" ? cached.src : "");
        return;
      }

      setResolutionState("loading");
      for (const candidate of candidates) {
        const ok = await probeImageUrl(candidate);
        if (cancelled) return;
        if (ok) {
          imageResolutionCache.set(cacheKey, { status: "resolved", src: candidate });
          setResolutionState("resolved");
          setResolvedSrc(candidate);
          return;
        }
      }

      imageResolutionCache.set(cacheKey, { status: "missing" });
      setResolutionState("missing");
      setResolvedSrc("");
    }

    resolveImage();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, candidates, isInView]);

  if (!isInView || !resolvedSrc) {
    return (
      <div ref={containerRef} className="lego-thumb-placeholder">
        {resolutionState === "missing" ? "N/A" : ""}
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <img
        className="lego-thumb"
        src={resolvedSrc}
        alt={`LEGO ${legoId}`}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export default LegoImage;
